import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import ModuleLayout from '../components/ModuleLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, UserPlus, Shield, Pencil, Trash2, Mail, Building2,
  Search, KeyRound, History, Loader2, Calendar, Activity,
  Power, Eye, ChevronDown
} from 'lucide-react';

const ROLE_COLORS = {
  owner: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  manager: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  cashier: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  chef: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  accountant: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  kitchen: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
};

export default function TeamPage() {
  const { authHeaders, API, branches, user } = useAuth();
  const { t } = useTranslation();
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [showActivity, setShowActivity] = useState(null);
  const [activityData, setActivityData] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'cashier', branch_ids: [] });
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchData = async () => {
    try {
      const [membersRes, rolesRes] = await Promise.all([
        axios.get(`${API}/team`, authHeaders()),
        axios.get(`${API}/team/roles`, authHeaders()),
      ]);
      setMembers(membersRes.data.members || []);
      setRoles(rolesRes.data.roles || []);
    } catch { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const filteredMembers = useMemo(() => {
    return members.filter(m => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!m.name.toLowerCase().includes(q) && !m.email.toLowerCase().includes(q)) return false;
      }
      if (roleFilter !== 'all' && m.role !== roleFilter) return false;
      if (statusFilter === 'active' && m.is_active === false) return false;
      if (statusFilter === 'inactive' && m.is_active !== false) return false;
      return true;
    });
  }, [members, searchQuery, roleFilter, statusFilter]);

  const handleInvite = async () => {
    if (!form.name || !form.email || !form.password) return;
    setSaving(true);
    try {
      await axios.post(`${API}/team/invite`, {
        name: form.name, email: form.email, password: form.password,
        role: form.role, branch_ids: form.branch_ids,
      }, authHeaders());
      toast.success(t('team.inviteSuccess'));
      setShowInvite(false);
      setForm({ name: '', email: '', password: '', role: 'cashier', branch_ids: [] });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    setSaving(false);
  };

  const handleUpdate = async () => {
    if (!editMember) return;
    setSaving(true);
    try {
      await axios.put(`${API}/team/${editMember.id}`, {
        name: form.name, role: form.role, branch_ids: form.branch_ids, is_active: form.is_active,
      }, authHeaders());
      toast.success(t('team.updateSuccess'));
      setEditMember(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('team.deleteConfirm'))) return;
    try {
      await axios.delete(`${API}/team/${id}`, authHeaders());
      toast.success(t('team.deleteSuccess'));
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleResetPassword = async () => {
    if (!showResetPassword || newPassword.length < 6) return;
    setSaving(true);
    try {
      await axios.post(`${API}/team/${showResetPassword.id}/reset-password`, { new_password: newPassword }, authHeaders());
      toast.success(t('team.resetPasswordSuccess'));
      setShowResetPassword(null);
      setNewPassword('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    setSaving(false);
  };

  const handleChangeMyPassword = async () => {
    if (pwForm.next.length < 6) { toast.error(t('auth.passwordTooShort')); return; }
    if (!/^[\x21-\x7E]+$/.test(pwForm.next)) { toast.error(t('auth.passwordLatinOnly')); return; }
    if (pwForm.next !== pwForm.confirm) { toast.error(t('account.passwordMismatch')); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: pwForm.current, new_password: pwForm.next }, authHeaders());
      toast.success(t('account.passwordChanged'));
      setShowChangePassword(false);
      setPwForm({ current: '', next: '', confirm: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    setSaving(false);
  };

  const handleToggleStatus = async (member) => {
    try {
      const newStatus = member.is_active === false ? true : false;
      await axios.post(`${API}/team/${member.id}/toggle-status`, { is_active: newStatus }, authHeaders());
      toast.success(t('team.toggleStatusSuccess'));
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const viewMemberActivity = async (member) => {
    setShowActivity(member);
    setActivityLoading(true);
    try {
      const res = await axios.get(`${API}/team/${member.id}/activity?limit=30`, authHeaders());
      setActivityData(res.data);
    } catch { toast.error('Error loading activity'); }
    setActivityLoading(false);
  };

  const openEdit = (member) => {
    setEditMember(member);
    setForm({ name: member.name, email: member.email, password: '', role: member.role, branch_ids: member.branch_ids || [], is_active: member.is_active !== false });
  };

  const getRoleName = (roleKey) => t(`team.roles.${roleKey}`, roleKey);

  const toggleBranch = (branchId) => {
    setForm(prev => ({
      ...prev,
      branch_ids: prev.branch_ids.includes(branchId) ? prev.branch_ids.filter(b => b !== branchId) : [...prev.branch_ids, branchId]
    }));
  };

  const roleOptions = roles.filter(r => r.key !== 'owner');
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('ar-SA', { month: 'short', day: 'numeric' }) : t('team.never');

  return (
    <ModuleLayout moduleKey="settings">
      <div className="p-4 md:p-6 space-y-6 max-w-5xl mx-auto" data-testid="team-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="team-title">
              <Users className="w-6 h-6 text-primary" /> {t('team.title')}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">{t('team.subtitle')}</p>
          </div>
          <Button onClick={() => { setShowInvite(true); setForm({ name: '', email: '', password: '', role: 'cashier', branch_ids: [] }); }}
            data-testid="invite-member-btn" className="gap-2">
            <UserPlus className="w-4 h-4" /> {t('team.inviteMember')}
          </Button>
        </div>

        {/* Account & Security */}
        <Card className="border-border/50" data-testid="account-security-card">
          <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-sm">{t('account.title')}</p>
                <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5" data-testid="account-owner-email">
                  <Mail className="w-3 h-3" /> <span dir="ltr">{user?.email}</span>
                  <Badge variant="outline" className="text-[10px]">{getRoleName(user?.role || 'owner')}</Badge>
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" className="gap-2" onClick={() => setShowChangePassword(true)} data-testid="change-my-password-btn">
              <KeyRound className="w-4 h-4" /> {t('account.changePassword')}
            </Button>
          </CardContent>
        </Card>

        {/* Role Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {roles.filter(r => r.key !== 'owner').map(role => {
            const count = members.filter(m => m.role === role.key).length;
            return (
              <Card key={role.key} className="border-border/50 overflow-hidden transition-all hover:shadow-sm cursor-pointer"
                data-testid={`role-card-${role.key}`}
                onClick={() => setRoleFilter(roleFilter === role.key ? 'all' : role.key)}>
                <div className="h-0.5" style={{ background: role.color || '#6B7280' }} />
                <CardContent className="p-4 text-center">
                  <div className="w-9 h-9 rounded-lg mx-auto mb-2 flex items-center justify-center" style={{ background: `${role.color || '#6B7280'}15` }}>
                    <Shield className="w-4 h-4" style={{ color: role.color }} />
                  </div>
                  <div className="text-2xl font-bold" style={{ color: role.color }}>{count}</div>
                  <div className="text-[11px] text-muted-foreground mt-1">{role.name_ar}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={t('team.searchMembers')}
              className="ps-10"
              data-testid="search-members"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-full sm:w-44" data-testid="role-filter">
              <SelectValue placeholder={t('team.filterByRole')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('team.allRoles')}</SelectItem>
              {roleOptions.map(r => <SelectItem key={r.key} value={r.key}>{r.name_ar}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-36" data-testid="status-filter">
              <SelectValue placeholder={t('team.filterByStatus')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('team.allStatuses')}</SelectItem>
              <SelectItem value="active">{t('team.active')}</SelectItem>
              <SelectItem value="inactive">{t('team.inactive')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Members Table */}
        <Card className="border-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="w-5 h-5" /> {t('team.title')} ({filteredMembers.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : filteredMembers.length === 0 ? (
              <div className="text-center py-12" data-testid="no-members">
                <Users className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
                <p className="font-medium text-muted-foreground">{t('team.noMembers')}</p>
                <p className="text-sm text-muted-foreground/70 mt-1">{t('team.noMembersDesc')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border/50">
                      <th className="text-start p-3 font-medium text-muted-foreground">{t('team.name')}</th>
                      <th className="text-start p-3 font-medium text-muted-foreground">{t('team.role')}</th>
                      <th className="text-start p-3 font-medium text-muted-foreground">{t('team.status')}</th>
                      <th className="text-start p-3 font-medium text-muted-foreground">{t('team.lastActivity')}</th>
                      <th className="text-start p-3 font-medium text-muted-foreground">{t('team.activityCount')}</th>
                      <th className="text-start p-3 font-medium text-muted-foreground">{t('team.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMembers.map(member => (
                      <tr key={member.id} className="border-b border-border/30 hover:bg-muted/30 transition-colors"
                        data-testid={`team-member-${member.id}`}>
                        <td className="p-3">
                          <div>
                            <span className="font-medium">{member.name}</span>
                            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                              <Mail className="w-3 h-3" /> {member.email}
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <Badge className={ROLE_COLORS[member.role] || 'bg-gray-100 text-gray-800'} data-testid={`role-badge-${member.role}`}>
                            {getRoleName(member.role)}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={member.is_active !== false}
                              onCheckedChange={() => handleToggleStatus(member)}
                              className="scale-75"
                              data-testid={`toggle-status-${member.id}`}
                            />
                            <span className={`text-xs ${member.is_active !== false ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                              {member.is_active !== false ? t('team.active') : t('team.inactive')}
                            </span>
                          </div>
                        </td>
                        <td className="p-3">
                          <span className="text-xs text-muted-foreground">
                            {formatDate(member.last_activity)}
                          </span>
                        </td>
                        <td className="p-3">
                          <Badge variant="outline" className="text-xs">
                            <Activity className="w-3 h-3 me-1" />
                            {member.activity_count || 0}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openEdit(member)}
                              data-testid={`edit-member-${member.id}`} className="h-8 w-8 p-0" title={t('team.editMember')}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => { setShowResetPassword(member); setNewPassword(''); }}
                              data-testid={`reset-pwd-${member.id}`} className="h-8 w-8 p-0" title={t('team.resetPassword')}>
                              <KeyRound className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => viewMemberActivity(member)}
                              data-testid={`view-activity-${member.id}`} className="h-8 w-8 p-0" title={t('team.viewActivity')}>
                              <History className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(member.id)}
                              data-testid={`delete-member-${member.id}`}
                              className="h-8 w-8 p-0 text-destructive hover:text-destructive" title={t('common.delete')}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Invite Dialog */}
        <Dialog open={showInvite} onOpenChange={setShowInvite}>
          <DialogContent className="sm:max-w-md" data-testid="invite-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5" /> {t('team.inviteMember')}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div><Label>{t('team.name')}</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="invite-name" placeholder={t('team.name')} /></div>
              <div><Label>{t('team.email')}</Label>
                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="invite-email" placeholder={t('team.email')} /></div>
              <div><Label>{t('team.password')}</Label>
                <Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="invite-password" placeholder={t('team.password')} /></div>
              <div><Label>{t('team.role')}</Label>
                <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{roleOptions.map(r => <SelectItem key={r.key} value={r.key}>{r.name_ar} ({r.name_en})</SelectItem>)}</SelectContent>
                </Select></div>
              {branches.length > 0 && (
                <div><Label>{t('team.branches')}</Label>
                  <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                    {branches.map(b => (
                      <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/30 p-1.5 rounded">
                        <input type="checkbox" className="rounded" checked={form.branch_ids.includes(b.id)} onChange={() => toggleBranch(b.id)} />
                        <Building2 className="w-3.5 h-3.5 text-muted-foreground" /> {b.name}
                      </label>))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{form.branch_ids.length === 0 && t('team.allBranches')}</p>
                </div>)}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowInvite(false)}>{t('common.cancel')}</Button>
              <Button onClick={handleInvite} disabled={saving || !form.name || !form.email || !form.password} data-testid="invite-submit">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('team.inviteMember')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={!!editMember} onOpenChange={() => setEditMember(null)}>
          <DialogContent className="sm:max-w-md" data-testid="edit-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Pencil className="w-5 h-5" /> {t('team.editMember')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div><Label>{t('team.name')}</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="edit-name" /></div>
              <div><Label>{t('team.role')}</Label>
                <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="edit-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{roleOptions.map(r => <SelectItem key={r.key} value={r.key}>{r.name_ar} ({r.name_en})</SelectItem>)}</SelectContent>
                </Select></div>
              {branches.length > 0 && (
                <div><Label>{t('team.branches')}</Label>
                  <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                    {branches.map(b => (
                      <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/30 p-1.5 rounded">
                        <input type="checkbox" className="rounded" checked={form.branch_ids.includes(b.id)} onChange={() => toggleBranch(b.id)} />
                        <Building2 className="w-3.5 h-3.5 text-muted-foreground" /> {b.name}
                      </label>))}
                  </div></div>)}
              <div className="flex items-center justify-between p-3 rounded-xl border border-border/50 bg-muted/20">
                <div className="flex items-center gap-2">
                  <Power className="w-4 h-4 text-muted-foreground" />
                  <Label className="mb-0">{t('team.status')}</Label>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${form.is_active ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                    {form.is_active ? t('team.active') : t('team.inactive')}
                  </span>
                  <Switch checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} data-testid="edit-active-toggle" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditMember(null)}>{t('common.cancel')}</Button>
              <Button onClick={handleUpdate} disabled={saving} data-testid="edit-submit">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Change My Password Dialog */}
        <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
          <DialogContent className="sm:max-w-sm" data-testid="change-password-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5" /> {t('account.changePassword')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div><Label>{t('account.currentPassword')}</Label>
                <Input type="password" dir="ltr" value={pwForm.current} onChange={e => setPwForm({ ...pwForm, current: e.target.value })} data-testid="current-password-input" /></div>
              <div><Label>{t('account.newPassword')}</Label>
                <Input type="password" dir="ltr" value={pwForm.next} onChange={e => setPwForm({ ...pwForm, next: e.target.value })} data-testid="my-new-password-input" />
                <p className="text-[11px] text-muted-foreground mt-1">{t('auth.passwordHint')}</p></div>
              <div><Label>{t('account.confirmPassword')}</Label>
                <Input type="password" dir="ltr" value={pwForm.confirm} onChange={e => setPwForm({ ...pwForm, confirm: e.target.value })} data-testid="confirm-password-input" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowChangePassword(false)}>{t('common.cancel')}</Button>
              <Button onClick={handleChangeMyPassword} disabled={saving || !pwForm.current || !pwForm.next} data-testid="change-password-submit"
                className="bg-amber-500 hover:bg-amber-600 text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={!!showResetPassword} onOpenChange={() => setShowResetPassword(null)}>
          <DialogContent className="sm:max-w-sm" data-testid="reset-password-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5" /> {t('team.resetPassword')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {showResetPassword && (
                <div className="p-3 rounded-xl bg-muted/30 border border-border/50">
                  <p className="text-sm font-medium">{showResetPassword.name}</p>
                  <p className="text-xs text-muted-foreground">{showResetPassword.email}</p>
                </div>
              )}
              <div>
                <Label>{t('team.newPassword')}</Label>
                <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                  data-testid="new-password-input" placeholder="6+ characters" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowResetPassword(null)}>{t('common.cancel')}</Button>
              <Button onClick={handleResetPassword} disabled={saving || newPassword.length < 6} data-testid="reset-password-submit"
                className="bg-amber-500 hover:bg-amber-600 text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('team.resetPassword')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Activity Dialog */}
        <Dialog open={!!showActivity} onOpenChange={() => { setShowActivity(null); setActivityData(null); }}>
          <DialogContent className="sm:max-w-lg max-h-[80vh]" data-testid="activity-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="w-5 h-5" /> {t('team.memberActivity')}
                {showActivity && <Badge variant="outline" className="ms-2 text-xs">{showActivity.name}</Badge>}
              </DialogTitle>
            </DialogHeader>
            <div className="py-2">
              {activityLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : !activityData || activityData.activities?.length === 0 ? (
                <div className="text-center py-8">
                  <History className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">{t('team.noActivity')}</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto pe-1">
                  {activityData.activities.map((act, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-xl border border-border/30 hover:bg-muted/20 transition-colors"
                      data-testid={`activity-item-${i}`}>
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Activity className="w-4 h-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{act.action}</p>
                        {act.details && <p className="text-xs text-muted-foreground mt-0.5">{act.details}</p>}
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-[10px]">{act.category}</Badge>
                          <span className="text-[10px] text-muted-foreground">
                            <Calendar className="w-3 h-3 inline me-0.5" />
                            {act.created_at ? new Date(act.created_at).toLocaleString('ar-SA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {activityData?.total > 0 && (
                <p className="text-center text-xs text-muted-foreground mt-3">
                  {t('team.activityCount')}: {activityData.total}
                </p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </ModuleLayout>
  );
}
